import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../assets/ADCreate.css';
import {
  fetchAdConfigurations,
  fetchAdAnomalyTypes,
  fetchAdHandleOptions,
  createAdConfiguration,
  updateAdConfiguration,
  getAdConfiguration,
} from '../api/restapi';

let hSeq = 0;

function newHandleRow(group) {
  return {
    _id: 'hr_' + (++hSeq),
    handleMasterId: '',
    triggerGroup: group,
    triggerClass: '',
    triggerName: '',
    enabled: true,
    val: '', trigOnBelow: true, stayCheck: false, consec: '', rise: false,
    count: '', window: '',
    ccType: 'Any CC', techType: 'N/A', subType: 'N/A',
  };
}

function hydrateRow(handle, group) {
  const cv = handle.classValues || {};
  return {
    _id: 'hr_' + (++hSeq),
    handleMasterId: handle.handleMasterId != null ? handle.handleMasterId : '',
    triggerGroup: handle.triggerGroup || group,
    triggerClass: '',
    triggerName: '',
    enabled: handle.enabled !== false,
    val:         cv.val         != null ? cv.val         : '',
    trigOnBelow: cv.trigOnBelow != null ? cv.trigOnBelow : true,
    stayCheck:   cv.stayCheck   != null ? cv.stayCheck   : false,
    consec:      cv.consec      != null ? cv.consec      : '',
    rise:        cv.rise        != null ? cv.rise        : false,
    count:       cv.count       != null ? cv.count       : '',
    window:      cv.window      != null ? cv.window      : '',
    ccType:   cv.ccType   || 'Any CC',
    techType: cv.techType || 'N/A',
    subType:  cv.subType  || 'N/A',
  };
}

const EMPTY_FORM = {
  label: '', adId: '', description: '', technology: '', platform: '',
  status: 'ENABLED',
  stopTimerEnabled: false,
  stopTimerDuration: '', stopTimerUnit: 'SECONDS', stopTimerEvent: '',
};

function ADConfigManagement({ userInfo, preselectedAdId }) {
  const formTopRef = useRef(null);

  const [configs, setConfigs]         = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError]     = useState('');

  const [editId, setEditId]     = useState(null);
  const [editLabel, setEditLabel] = useState('');

  const [form, setForm]           = useState(EMPTY_FORM);
  const [preHandles, setPreHandles]   = useState([]);
  const [mainHandles, setMainHandles] = useState([]);
  const [activeTab, setActiveTab] = useState('pre');

  const [anomalyTypes, setAnomalyTypes]               = useState([]);
  const [handleOptions, setHandleOptions]             = useState({ PRE: [], MAIN: [] });
  const [anomalyTypesLoading, setAnomalyTypesLoading] = useState(false);
  const [handleOptionsLoading, setHandleOptionsLoading] = useState(false);

  const [bannerMsgs, setBannerMsgs]       = useState([]);
  const [showBanner, setShowBanner]       = useState(false);
  const [bannerSuccess, setBannerSuccess] = useState(false);
  const [saving, setSaving]               = useState(false);

  const user = (typeof userInfo === 'string' ? userInfo : userInfo?.mail || userInfo?.email) || 'unknown';

  const loadConfigs = useCallback(async () => {
    setListLoading(true);
    setListError('');
    const data = await fetchAdConfigurations(preselectedAdId);
    if (data && data.errorMessage) {
      setListError(data.errorMessage);
      setConfigs([]);
    } else {
      setConfigs(Array.isArray(data) ? data : []);
    }
    setListLoading(false);
  }, [preselectedAdId]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  useEffect(() => {
    setAnomalyTypesLoading(true);
    fetchAdAnomalyTypes().then(data => {
      setAnomalyTypes(Array.isArray(data) ? data : []);
      setAnomalyTypesLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!form.adId) { setHandleOptions({ PRE: [], MAIN: [] }); return; }
    setHandleOptionsLoading(true);
    Promise.all([
      fetchAdHandleOptions(form.adId, 'PRE'),
      fetchAdHandleOptions(form.adId, 'MAIN'),
    ]).then(([pre, main]) => {
      setHandleOptions({
        PRE:  Array.isArray(pre)  ? pre  : [],
        MAIN: Array.isArray(main) ? main : [],
      });
      setHandleOptionsLoading(false);
    });
  }, [form.adId]);

  // Resolve triggerClass/triggerName for hydrated rows after options load
  useEffect(() => {
    if (!editId) return;
    const resolve = (rows, group) =>
      rows.map(row => {
        if (row.triggerClass) return row;
        const opt = handleOptions[group].find(
          o => String(o.handleMasterId) === String(row.handleMasterId)
        );
        if (!opt) return row;
        return { ...row, triggerClass: opt.triggerClass || '', triggerName: opt.triggerName || '' };
      });
    setPreHandles(prev => resolve(prev, 'PRE'));
    setMainHandles(prev => resolve(prev, 'MAIN'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOptions, editId]);

  const showMsg = (msgs, success = false) => {
    setBannerMsgs(Array.isArray(msgs) ? msgs : [msgs]);
    setBannerSuccess(success);
    setShowBanner(true);
    if (success) setTimeout(() => setShowBanner(false), 4000);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setPreHandles([]);
    setMainHandles([]);
    setActiveTab('pre');
    setShowBanner(false);
    setEditId(null);
    setEditLabel('');
  };

  const openEdit = async (id, label) => {
    setShowBanner(false);
    const data = await getAdConfiguration(id);
    if (data && data.errorMessage) {
      setListError(`Failed to load configuration: ${data.errorMessage}`);
      return;
    }
    setEditId(id);
    setEditLabel(label || '');
    setForm({
      label:       data.label        || '',
      adId:        data.adId   != null ? String(data.adId) : '',
      description: data.description  || '',
      technology:  data.technology   || '',
      platform:    data.platform     || '',
      status:      data.status       || 'ENABLED',
      stopTimerEnabled:  !!data.stopTimerEnabled,
      stopTimerDuration: data.stopTimerDuration != null ? String(data.stopTimerDuration) : '',
      stopTimerUnit:     data.stopTimerUnit     || 'SECONDS',
      stopTimerEvent:    data.stopTimerEvent    || '',
    });
    const all = Array.isArray(data.handles) ? data.handles : [];
    setPreHandles(all.filter(h => h.triggerGroup === 'PRE').map(h => hydrateRow(h, 'PRE')));
    setMainHandles(all.filter(h => h.triggerGroup === 'MAIN').map(h => hydrateRow(h, 'MAIN')));
    setActiveTab('pre');
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const setF = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const onAdIdChange = (val) => {
    const at = anomalyTypes.find(t => String(t.adId) === String(val));
    setForm(prev => ({ ...prev, adId: val, technology: at?.technology || '' }));
    setPreHandles([]);
    setMainHandles([]);
  };

  const updateRow = useCallback((group, rowId, fields) => {
    (group === 'PRE' ? setPreHandles : setMainHandles)(
      prev => prev.map(r => r._id === rowId ? { ...r, ...fields } : r)
    );
  }, []);

  const addRow = (group) => {
    (group === 'PRE' ? setPreHandles : setMainHandles)(
      prev => [...prev, newHandleRow(group)]
    );
  };

  const deleteRow = (group, rowId) => {
    (group === 'PRE' ? setPreHandles : setMainHandles)(
      prev => prev.filter(r => r._id !== rowId)
    );
  };

  const onHandleOptionSelect = useCallback((group, rowId, value) => {
    const opts = group === 'PRE' ? handleOptions.PRE : handleOptions.MAIN;
    const opt  = opts.find(o => String(o.handleMasterId) === String(value));
    if (!opt) {
      updateRow(group, rowId, { handleMasterId: value, triggerClass: '', triggerName: '' });
      return;
    }
    let cv = {};
    if (opt.defaultConfig) {
      try { cv = JSON.parse(opt.defaultConfig); } catch { /* ignore */ }
    }
    updateRow(group, rowId, {
      handleMasterId: opt.handleMasterId,
      triggerClass:   opt.triggerClass  || '',
      triggerName:    opt.triggerName   || '',
      val:         cv.val         != null ? cv.val         : '',
      trigOnBelow: cv.trigOnBelow != null ? cv.trigOnBelow : true,
      stayCheck:   cv.stayCheck   != null ? cv.stayCheck   : false,
      consec:      cv.consec      != null ? cv.consec      : '',
      rise:        cv.rise        != null ? cv.rise        : false,
      count:       cv.count       != null ? cv.count       : '',
      window:      cv.window      != null ? cv.window      : '',
    });
  }, [handleOptions, updateRow]);

  const buildBody = () => {
    const buildHandles = (rows) => rows.map(row => {
      const cv = {};
      if (row.triggerClass === 'THRESHOLD') {
        const rawVal = parseFloat(row.val) || 0;
        cv.val         = Math.round(rawVal * (rawVal % 1 !== 0 ? 10 : 1));
        cv.multiplier  = rawVal % 1 !== 0 ? 10 : 1;
        cv.trigOnBelow = !!row.trigOnBelow;
        cv.stayCheck   = !!row.stayCheck;
        if (row.consec !== '') cv.consec = parseInt(row.consec, 10) || 0;
      } else if (row.triggerClass === 'DROP') {
        const rawVal = parseFloat(row.val) || 0;
        cv.val        = Math.round(rawVal * (rawVal % 1 !== 0 ? 10 : 1));
        cv.multiplier = rawVal % 1 !== 0 ? 10 : 1;
        cv.rise       = !!row.rise;
        cv.stayCheck  = !!row.stayCheck;
        if (row.consec !== '') cv.consec = parseInt(row.consec, 10) || 0;
      } else if (row.triggerClass === 'COUNT') {
        cv.count = parseInt(row.count, 10) || 0;
        if (row.window !== '') cv.window = parseInt(row.window, 10) || 0;
      }
      cv.ccType   = row.ccType   || 'Any CC';
      cv.techType = row.techType || 'N/A';
      cv.subType  = row.subType  || 'N/A';
      return {
        handleMasterId: row.handleMasterId,
        triggerGroup:   row.triggerGroup,
        classValues:    cv,
        enabled:        !!row.enabled,
      };
    });
    return {
      label:       form.label.trim(),
      adId:        Number(form.adId),
      description: form.description.trim() || null,
      technology:  form.technology || null,
      platform:    form.platform   || null,
      status:      form.status || 'ENABLED',
      stopTimerEnabled:  !!form.stopTimerEnabled,
      stopTimerDuration: form.stopTimerEnabled && form.stopTimerDuration !== ''
        ? parseInt(form.stopTimerDuration, 10) : null,
      stopTimerUnit:  form.stopTimerEnabled ? (form.stopTimerUnit || 'SECONDS') : null,
      stopTimerEvent: form.stopTimerEnabled && form.stopTimerEvent.trim()
        ? form.stopTimerEvent.trim() : null,
      handles: [...buildHandles(preHandles), ...buildHandles(mainHandles)],
    };
  };

  const validate = () => {
    const errs = [];
    if (!form.label.trim()) errs.push('Label is required.');
    if (!form.adId)         errs.push('Overall Anomaly selection is required.');
    if (mainHandles.filter(r => r.handleMasterId).length === 0)
      errs.push('At least one MAIN trigger handle is required.');
    if (form.stopTimerEnabled && (!form.stopTimerDuration || parseFloat(form.stopTimerDuration) <= 0))
      errs.push('Timer is enabled but Timer Duration is missing or invalid.');
    [...preHandles, ...mainHandles].forEach((r, i) => {
      if (!r.handleMasterId)
        errs.push(`Handle row ${i + 1} (${r.triggerGroup}) has no trigger selected.`);
    });
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (errs.length) { showMsg(errs); return; }
    setSaving(true);
    const body   = buildBody();
    const result = editId
      ? await updateAdConfiguration(editId, body, user)
      : await createAdConfiguration(body, user);
    setSaving(false);
    if (result && result.errorMessage) { showMsg([result.errorMessage]); return; }
    showMsg([editId ? '✅ Configuration updated successfully.' : '✅ Configuration created successfully.'], true);
    setTimeout(async () => { resetForm(); await loadConfigs(); }, 1000);
  };

  const renderClassFields = (row, group) => {
    const upd = (f, v) => updateRow(group, row._id, { [f]: v });
    if (row.triggerClass === 'THRESHOLD') return (
      <div className="config-grid">
        <div className="field">
          <label>Threshold Value <span className="req">*</span></label>
          <input type="number" className="inp" placeholder="e.g. 110" value={row.val}
            onChange={e => upd('val', e.target.value)} />
        </div>
        <div className="field">
          <label>Direction (trigOnBelow)</label>
          <select className="sel" value={row.trigOnBelow ? 'true' : 'false'}
            onChange={e => upd('trigOnBelow', e.target.value === 'true')}>
            <option value="true">Below Threshold</option>
            <option value="false">Above Threshold</option>
          </select>
        </div>
        <div className="field">
          <label>Consecutive Samples (consec)</label>
          <input type="number" className="inp" placeholder="e.g. 1" value={row.consec}
            onChange={e => upd('consec', e.target.value)} min="0" step="1" />
        </div>
        <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}>
          <input type="checkbox" id={'sc_' + row._id} checked={!!row.stayCheck}
            onChange={e => upd('stayCheck', e.target.checked)} />
          <label htmlFor={'sc_' + row._id} style={{ fontSize: 12.5, cursor: 'pointer' }}>
            Stay Check (stayCheck)
          </label>
        </div>
      </div>
    );
    if (row.triggerClass === 'DROP') return (
      <div className="config-grid">
        <div className="field">
          <label>Drop Value <span className="req">*</span></label>
          <input type="number" className="inp" placeholder="e.g. 25" value={row.val}
            onChange={e => upd('val', e.target.value)} />
        </div>
        <div className="field">
          <label>Direction (rise)</label>
          <select className="sel" value={row.rise ? 'true' : 'false'}
            onChange={e => upd('rise', e.target.value === 'true')}>
            <option value="false">Drop (rise = false)</option>
            <option value="true">Rise (rise = true)</option>
          </select>
        </div>
        <div className="field">
          <label>Consecutive Samples (consec)</label>
          <input type="number" className="inp" placeholder="e.g. 1" value={row.consec}
            onChange={e => upd('consec', e.target.value)} min="0" step="1" />
        </div>
        <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}>
          <input type="checkbox" id={'sc_' + row._id} checked={!!row.stayCheck}
            onChange={e => upd('stayCheck', e.target.checked)} />
          <label htmlFor={'sc_' + row._id} style={{ fontSize: 12.5, cursor: 'pointer' }}>
            Stay Check (stayCheck)
          </label>
        </div>
      </div>
    );
    if (row.triggerClass === 'COUNT') return (
      <div className="config-grid">
        <div className="field">
          <label>Count <span className="req">*</span></label>
          <input type="number" className="inp" placeholder="e.g. 5" value={row.count}
            onChange={e => upd('count', e.target.value)} />
        </div>
        <div className="field">
          <label>Window (seconds)</label>
          <input type="number" className="inp" placeholder="e.g. 60" value={row.window}
            onChange={e => upd('window', e.target.value)} />
        </div>
      </div>
    );
    return null;
  };

  const renderHandleRow = (row, group, opts, idx) => {
    const upd = (f, v) => updateRow(group, row._id, { [f]: v });
    const isDefaultMain = group === 'MAIN' && idx === 0;
    return (
      <div key={row._id} className="handle-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <span className="badge badge-blue" style={{ fontSize: 10 }}>Handle {idx + 1}</span>
          {row.triggerClass === 'THRESHOLD' && <span className="badge badge-thresh" style={{ fontSize: 10 }}>Threshold</span>}
          {row.triggerClass === 'DROP'      && <span className="badge badge-drop"   style={{ fontSize: 10 }}>Drop</span>}
          {row.triggerClass === 'COUNT'     && <span className="badge badge-count"  style={{ fontSize: 10 }}>Count</span>}
          {isDefaultMain && (
            <span className="badge" style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>
              ⭐ Default Main
            </span>
          )}
          <button className="del-btn" style={{ marginLeft: 'auto' }}
            onClick={() => deleteRow(group, row._id)} title="Remove handle">
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/>
            </svg>
          </button>
        </div>

        <div className="field" style={{ marginBottom: 10 }}>
          <label>
            Trigger Handle ({group}) <span className="req">*</span>
            {row.triggerName && (
              <span className="badge badge-gray" style={{ marginLeft: 6, fontSize: 10 }}>
                {row.triggerName}
              </span>
            )}
          </label>
          {opts.length === 0 && !handleOptionsLoading ? (
            <div style={{ fontSize: 12, color: 'var(--gray-400)', padding: '4px 0', fontStyle: 'italic' }}>
              {form.adId ? `No ${group} handle options available for this anomaly type.`
                         : 'Select an Overall Anomaly first.'}
            </div>
          ) : (
            <select className="sel"
              value={row.handleMasterId != null ? String(row.handleMasterId) : ''}
              onChange={e => onHandleOptionSelect(group, row._id, e.target.value)}
              disabled={handleOptionsLoading}>
              <option value="">— Select Trigger Handle —</option>
              {opts.map(o => (
                <option key={o.handleMasterId} value={String(o.handleMasterId)}>
                  {o.triggerName || `Handle ${o.handleMasterId}`}
                  {o.triggerClass ? ` [${o.triggerClass}]` : ''}
                  {o.required ? ' *' : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {row.triggerClass && (
          <div className="meta-grid" style={{ marginBottom: 10 }}>
            <div className="field">
              <div className="meta-legend">CC Type</div>
              <select className="sel" style={{ fontSize: 12 }} value={row.ccType}
                onChange={e => upd('ccType', e.target.value)}>
                <option>Any CC</option><option>Aggregate</option>
                <option>All CC</option><option>N/A</option>
              </select>
            </div>
            <div className="field">
              <div className="meta-legend">Tech Type</div>
              <select className="sel" style={{ fontSize: 12 }} value={row.techType}
                onChange={e => upd('techType', e.target.value)}>
                <option>N/A</option><option>NR</option><option>LTE</option>
              </select>
            </div>
            <div className="field">
              <div className="meta-legend">Sub Type</div>
              <select className="sel" style={{ fontSize: 12 }} value={row.subType}
                onChange={e => upd('subType', e.target.value)}>
                <option>N/A</option><option>Sub 0</option><option>Sub 1</option>
              </select>
            </div>
          </div>
        )}

        {row.triggerClass ? (
          <div className="config-block">
            <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 8 }}>
              ⚙ {row.triggerClass} Configuration
              <span className="badge badge-blue" style={{ fontSize: 10, marginLeft: 8 }}>Configurable</span>
            </div>
            {renderClassFields(row, group)}
          </div>
        ) : (
          <div style={{ fontSize: 11.5, color: 'var(--gray-400)', fontStyle: 'italic', padding: '4px 0' }}>
            {row.handleMasterId
              ? 'Loading trigger class…'
              : 'Select a trigger handle to configure parameters.'}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <label className="toggle">
            <input type="checkbox" checked={!!row.enabled}
              onChange={e => upd('enabled', e.target.checked)} />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">{row.enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
      </div>
    );
  };

  const preOpts  = handleOptions.PRE;
  const mainOpts = handleOptions.MAIN;

  return (
    <div className="ad-create-scope">
      <div className="page">

        {/* ── FORM SECTION ── */}
        <div ref={formTopRef}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div className="page-title" style={{ marginBottom: 0 }}>
              {editId ? `Edit AD Configuration${editLabel ? ` — ${editLabel}` : ''}` : 'Create AD Configuration'}
            </div>
            {editId && (
              <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={resetForm}>
                ✕ Cancel Edit
              </button>
            )}
          </div>
          <div className="page-sub" style={{ marginBottom: 16 }}>
            Define AD triggers and threshold conditions for anomaly detection.
          </div>

          {showBanner && (
            <div className="val-banner" style={bannerSuccess ? {
              background: 'var(--green-light)', borderColor: '#86efac', color: 'var(--green)',
            } : {}}>
              {bannerSuccess ? bannerMsgs[0] : (
                <>
                  <strong>Please resolve the following before saving:</strong>
                  <ul>{bannerMsgs.map((m, i) => <li key={i}>{m}</li>)}</ul>
                </>
              )}
            </div>
          )}

          {/* Section 1: AD Details */}
          <div className="card">
            <div className="card-hdr">
              <div className="step-badge">1</div>
              <h3>AD Details</h3>
            </div>
            <div className="card-body">
              <div className="form-grid form-grid-2" style={{ marginBottom: 14 }}>
                <div className="field">
                  <label>Label <span className="req">*</span></label>
                  <input type="text" className="inp"
                    placeholder="e.g. DL Throughput Drop – Site A"
                    value={form.label}
                    onChange={e => setF('label', e.target.value)} />
                </div>

                <div className="field">
                  <label>Overall Anomaly <span className="req">*</span></label>
                  <select className="sel" value={form.adId}
                    onChange={e => onAdIdChange(e.target.value)}
                    disabled={anomalyTypesLoading}>
                    <option value="">— Select Anomaly Type —</option>
                    {anomalyTypes.map(t => (
                      <option key={t.adId} value={String(t.adId)}>{t.name || t.adId}</option>
                    ))}
                  </select>
                  {anomalyTypesLoading && (
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Loading anomaly types…</div>
                  )}
                </div>

                <div className="field" style={{ gridColumn: 'span 2' }}>
                  <label>Description</label>
                  <textarea className="ta"
                    placeholder="Describe the purpose and expected behaviour of this anomaly detection configuration…"
                    value={form.description}
                    onChange={e => setF('description', e.target.value)} />
                </div>

                <div className="field">
                  <label>Status</label>
                  <div className="toggle-wrap" style={{ paddingTop: 8 }}>
                    <label className="toggle">
                      <input type="checkbox" checked={form.status === 'ENABLED'}
                        onChange={e => setF('status', e.target.checked ? 'ENABLED' : 'DISABLED')} />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">
                      {form.status === 'ENABLED' ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>

              {form.adId && (() => {
                const at = anomalyTypes.find(t => String(t.adId) === form.adId);
                if (!at) return null;
                return (
                  <div className="info-panel">
                    <div className="info-panel-title">
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"
                        viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                      </svg>
                      Anomaly Type Info
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 20px' }}>
                      <div className="info-panel-row"><span className="lbl">Anomaly ID:</span><span className="val">{at.adId}</span></div>
                      <div className="info-panel-row"><span className="lbl">Name:</span><span className="val">{at.name}</span></div>
                      {at.technology && <div className="info-panel-row"><span className="lbl">Technology:</span><span className="val">{at.technology}</span></div>}
                      {at.platform   && <div className="info-panel-row"><span className="lbl">Platform:</span><span className="val">{at.platform}</span></div>}
                      {at.description && (
                        <div className="info-panel-row" style={{ gridColumn: 'span 2' }}>
                          <span className="lbl">Description:</span><span className="val">{at.description}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Section 2: Trigger Configuration */}
          <div className="card">
            <div className="card-hdr">
              <div className="step-badge">2</div>
              <h3>Trigger Configuration</h3>
              <span className="badge badge-amber" style={{ marginLeft: 'auto' }}>Main Trigger Required</span>
            </div>
            <div className="card-body">
              <div className="trigger-tabs">
                {[
                  { key: 'pre',  label: 'Pre Trigger',    color: '#ca8a04', handles: preHandles },
                  { key: 'main', label: 'Main Trigger',   color: '#1d4ed8', handles: mainHandles },
                  { key: 'stop', label: 'Stop Condition', color: '#16a34a', handles: [] },
                ].map(t => (
                  <div key={t.key}
                    className={`ttab${activeTab === t.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(t.key)}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: t.color, display: 'inline-block' }}></span>
                    {t.label}
                    {t.key !== 'stop' && (
                      <span className={`cnt${t.handles.length === 0 ? ' zero' : ''}${t.key === 'main' && t.handles.length === 0 ? ' req' : ''}`}>
                        {t.handles.length}
                      </span>
                    )}
                    {t.key === 'main' && (
                      <span className="badge badge-red" style={{ fontSize: 10, padding: '1px 5px' }}>Required</span>
                    )}
                  </div>
                ))}
              </div>

              <div className={`tpanel${activeTab === 'pre' ? ' active' : ''}`}>
                <div className="trigger-helper">
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                  </svg>
                  <span>Conditions monitored <strong>before</strong> the main anomaly trigger. Optional.</span>
                </div>
                <button className="add-btn"
                  onClick={() => {
                    if (!form.adId) { showMsg(['Please select an Overall Anomaly first.']); return; }
                    addRow('PRE');
                  }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                  Add Pre Trigger
                </button>
                {handleOptionsLoading && form.adId && (
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>Loading trigger options…</div>
                )}
                <div style={{ marginTop: 10 }}>
                  {preHandles.length === 0
                    ? <div className="empty-handles">No pre-trigger handles configured. Click "Add" to begin.</div>
                    : preHandles.map((row, i) => renderHandleRow(row, 'PRE', preOpts, i))}
                </div>
              </div>

              <div className={`tpanel${activeTab === 'main' ? ' active' : ''}`}>
                <div className="trigger-helper">
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                  </svg>
                  <span>Primary condition that <strong>starts anomaly detection or collection</strong>. At least one MAIN trigger handle is required.</span>
                </div>
                <button className="add-btn"
                  onClick={() => {
                    if (!form.adId) { showMsg(['Please select an Overall Anomaly first.']); return; }
                    addRow('MAIN');
                  }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                  Add Main Trigger
                </button>
                {handleOptionsLoading && form.adId && (
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>Loading trigger options…</div>
                )}
                <div style={{ marginTop: 10 }}>
                  {mainHandles.length === 0
                    ? <div className="empty-handles">No main trigger handles configured. At least one is required.</div>
                    : mainHandles.map((row, i) => renderHandleRow(row, 'MAIN', mainOpts, i))}
                </div>
              </div>

              <div className={`tpanel${activeTab === 'stop' ? ' active' : ''}`}>
                <div className="trigger-helper">
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                  </svg>
                  <span>Configure a <strong>timer-based stop</strong> condition.</span>
                </div>
                <div className="timer-panel">
                  <div className="timer-panel-hdr">
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    ⏱ Timer-Based Stop Condition
                    <label className="toggle" style={{ marginLeft: 'auto' }}>
                      <input type="checkbox" checked={!!form.stopTimerEnabled}
                        onChange={e => setF('stopTimerEnabled', e.target.checked)} />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">{form.stopTimerEnabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  {form.stopTimerEnabled && (
                    <div className="timer-fields">
                      <div className="field">
                        <label>Timer Duration <span className="req">*</span></label>
                        <input type="number" className="inp" placeholder="e.g. 5" min="1"
                          value={form.stopTimerDuration}
                          onChange={e => setF('stopTimerDuration', e.target.value)} />
                      </div>
                      <div className="field">
                        <label>Unit</label>
                        <select className="sel" value={form.stopTimerUnit}
                          onChange={e => setF('stopTimerUnit', e.target.value)}>
                          <option value="SECONDS">Seconds</option>
                          <option value="MILLISECONDS">Milliseconds</option>
                          <option value="MINUTES">Minutes</option>
                        </select>
                      </div>
                      <div className="field">
                        <label>Stop Event / Condition <span style={{ color: 'var(--gray-400)', fontSize: 10 }}>(optional)</span></label>
                        <input type="text" className="inp" placeholder="e.g. RLC ACK received"
                          value={form.stopTimerEvent}
                          onChange={e => setF('stopTimerEvent', e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Inline save/cancel buttons */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 32 }}>
            <button className="btn btn-ghost" onClick={resetForm} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editId ? 'Update Configuration →' : 'Save Configuration →'}
            </button>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div style={{ borderTop: '2px solid #e2e8f0', marginBottom: 28 }} />

        {/* ── LIST SECTION ── */}
        <div className="page-title" style={{ marginBottom: 4 }}>All AD Configurations</div>
        <div className="page-sub" style={{ marginBottom: 14 }}>
          Click Edit to pre-fill the form above.
        </div>

        {listError && <div className="val-banner">{listError}</div>}

        {listLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--gray-400)' }}>
            Loading configurations…
          </div>
        ) : configs.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="empty-handles" style={{ padding: '40px 0' }}>
                No AD configurations yet. Use the form above to create one.
              </div>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body" style={{ padding: 0 }}>
              <table className="view-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Anomaly ID</th>
                    <th>Status</th>
                    <th>Handles</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map(c => (
                    <tr key={c.id} style={editId === c.id ? { background: '#eff6ff' } : {}}>
                      <td style={{ fontWeight: 500 }}>{c.label}</td>
                      <td>{c.adId}</td>
                      <td>
                        <span className={`status-dot ${c.status === 'ENABLED' ? 'on' : 'off'}`}></span>
                        {c.status}
                      </td>
                      <td>{Array.isArray(c.handles) ? c.handles.length : '—'}</td>
                      <td>
                        <button className="btn btn-secondary"
                          style={{ padding: '4px 12px', fontSize: 12 }}
                          onClick={() => openEdit(c.id, c.label)}>
                          {editId === c.id ? 'Editing…' : 'Edit'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ADConfigManagement;
